"use client";

import type { ReactNode } from "react";
import { useCallback, useMemo, useRef, useSyncExternalStore } from "react";

import type {
  ConsentChoice,
  ConsentScope,
  ConsentState,
  ConsentStatus,
} from "@/core/consent/domain/consent.types";
import {
  getConsentStatusSnapshot,
  reopenConsentBanner,
  subscribeToConsentStatus,
} from "@/core/consent/engine/consent.runtime";
import { ConsentContext, defaultConsentState } from "@/core/consent/react/ConsentContext";
import type { ConsentContextValue } from "@/core/consent/react/ConsentContext";

export { ConsentContext, defaultConsentState } from "@/core/consent/react/ConsentContext";
export type { ConsentContextValue, ConsentSource } from "@/core/consent/react/ConsentContext";

type ConsentProviderProps = {
  readonly children: ReactNode;
};

const getServerSnapshot = (): ConsentStatus | null => null;

/**
 * The scopes Global Privacy Control governs, as the engine defines them (see
 * `clampToGpc` in the reducer).
 */
const isGpcGovernedScope = (scope: ConsentScope): boolean =>
  scope === "analytics" || scope === "advertising" || scope === "dataSharing";

const toResolution = (
  status: ConsentStatus | null,
): Pick<ConsentContextValue, "state" | "source" | "isResolved"> => {
  if (status?.status !== "resolved") {
    return { state: defaultConsentState, source: "default", isResolved: false };
  }

  return { state: { ...status.decision }, source: "cmp", isResolved: true };
};

/**
 * The app's consent API: a read-only projection of the consent engine's
 * status onto the context, plus the one action a visitor may take (reopen the
 * banner). Mounted from the root layout around the whole app, so every route
 * group — including the provider-less prelaunch home page — gets it.
 *
 * Read-only by construction: there are no setters, so the state consumers act
 * on is always the state the engine resolved and the recorder recorded. The
 * engine itself is reached through `consent.runtime`, never through context,
 * because it mounts beside the app tree rather than around it.
 */
export default function ConsentProvider({ children }: ConsentProviderProps): ReactNode {
  const status = useSyncExternalStore(
    subscribeToConsentStatus,
    getConsentStatusSnapshot,
    getServerSnapshot,
  );
  const { state, source, isResolved } = useMemo(() => toResolution(status), [status]);

  // Latest-value refs so getConsent/getConsentChoice are STABLE functions
  // reading current state: a submit callback in flight (a contact save
  // awaiting OTP verification) holds the closure from the render that created
  // it, and must still observe a decision made after that render.
  const stateRef = useRef<ConsentState>(state);
  stateRef.current = state;
  const statusRef = useRef<ConsentStatus | null>(status);
  statusRef.current = status;

  const getConsent = useCallback((): ConsentState => stateRef.current, []);

  const hasConsent = useCallback((scope: ConsentScope): boolean => state[scope] === true, [state]);

  const getConsentChoice = useCallback((scope: ConsentScope): ConsentChoice => {
    const current = statusRef.current;
    // A banner policy's default governs vendors, but it is not the visitor's choice.
    if (current?.status === "resolved" && current.answerProvenance !== "policyDefault") {
      return current.decision[scope];
    }

    // A GPC browser has explicitly opted out of the governed scopes even when
    // the banner was never answered; everything else is "not answered yet".
    return current?.gpcAsserted === true && isGpcGovernedScope(scope) ? false : null;
  }, []);

  const value = useMemo<ConsentContextValue>(
    () => ({
      state,
      source,
      isResolved,
      status,
      getConsent,
      hasConsent,
      getConsentChoice,
      reopenBanner: reopenConsentBanner,
    }),
    [state, source, isResolved, status, getConsent, hasConsent, getConsentChoice],
  );

  return <ConsentContext.Provider value={value}>{children}</ConsentContext.Provider>;
}
